import { MapPin, Phone, Mail, Footprints } from "lucide-react";
import { FadeUp, WhatsAppIcon } from "./Reveal";
import { useLang } from "../i18n/LanguageContext";
import { SITE, waLink } from "../data/site";

const MAPS_DIR = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(SITE.address)}`;

export default function LocationSection() {
  const { T } = useLang();
  return (
    <section id="ubicacion" data-testid="location-section" className="bg-federasol-blush/60 py-14 md:py-24">
      <div className="container-x grid items-stretch gap-10 lg:grid-cols-[1fr_1.25fr]">
        <FadeUp className="flex">
          <div className="flex w-full flex-col justify-center rounded-3xl border border-federasol-terra/10 bg-federasol-cream p-6 shadow-[0_14px_40px_-18px_rgba(64,49,47,0.15)] sm:p-10">
            <p className="eyebrow">{T.location.eyebrow}</p>
            <h2 className="mt-4 font-serif text-3xl font-medium leading-tight text-federasol-cocoa sm:text-4xl">
              {T.location.title}
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-federasol-muted">
              {T.location.subtitle}
            </p>

            <ul className="mt-6 space-y-4 text-sm sm:mt-8 sm:space-y-5">
              <li className="flex items-start gap-3.5">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-federasol-blush text-federasol-terra">
                  <MapPin className="h-5 w-5" strokeWidth={1.7} />
                </span>
                <span data-testid="contact-address-info" className="pt-2 font-medium text-federasol-cocoa">
                  {SITE.address}
                </span>
              </li>
              <li className="flex items-start gap-3.5">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-federasol-blush text-federasol-terra">
                  <Phone className="h-5 w-5" strokeWidth={1.7} />
                </span>
                <span className="pt-1.5">
                  <span className="block text-xs uppercase tracking-wider text-federasol-muted">{T.location.phone}</span>
                  <a
                    href={SITE.phoneHref}
                    data-testid="contact-phone-link"
                    className="font-semibold text-federasol-cocoa transition-colors hover:text-federasol-terra"
                  >
                    {SITE.phoneDisplay}
                  </a>
                </span>
              </li>
              <li className="flex items-start gap-3.5">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-federasol-blush text-federasol-terra">
                  <WhatsAppIcon className="h-5 w-5" />
                </span>
                <span className="pt-1.5">
                  <span className="block text-xs uppercase tracking-wider text-federasol-muted">{T.location.whatsapp}</span>
                  <a
                    href={waLink(T.wa.default)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-whatsapp-link"
                    className="font-semibold text-federasol-cocoa transition-colors hover:text-federasol-terra"
                  >
                    {SITE.whatsappDisplay}
                  </a>
                </span>
              </li>
              <li className="flex items-start gap-3.5">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-federasol-blush text-federasol-terra">
                  <Mail className="h-5 w-5" strokeWidth={1.7} />
                </span>
                <span className="pt-1.5">
                  <span className="block text-xs uppercase tracking-wider text-federasol-muted">{T.location.email}</span>
                  <a
                    href={SITE.emailHref}
                    data-testid="contact-email-link"
                    className="font-semibold text-federasol-cocoa transition-colors hover:text-federasol-terra"
                  >
                    {SITE.email}
                  </a>
                </span>
              </li>
            </ul>

            <div className="mt-6 flex flex-col gap-3 sm:mt-9 sm:flex-row">
              <a
                href={MAPS_DIR}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="directions-button"
                className="btn-outline flex-1"
              >
                {T.location.howTo}
              </a>
              <a
                href={waLink(T.wa.default)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="location-whatsapp-button"
                className="btn-whatsapp flex-1"
              >
                <WhatsAppIcon className="h-4 w-4" />
                {T.location.waBtn}
              </a>
            </div>
          </div>
        </FadeUp>

        <FadeUp delay={0.12}>
          <div
            data-testid="google-map-embed"
            className="h-[300px] overflow-hidden rounded-3xl border border-federasol-terra/10 shadow-[0_24px_60px_-24px_rgba(64,49,47,0.3)] sm:h-[420px] lg:h-full lg:min-h-[520px]"
          >
            <iframe
              title={`Hotel Federasol · ${T.location.title}`}
              src={`https://www.google.com/maps?q=${encodeURIComponent(SITE.address)}&output=embed&hl=es`}
              className="h-full w-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </FadeUp>
      </div>

      {/* Strategic location — verified distances */}
      <div className="container-x">
        <FadeUp delay={0.15} className="mt-10">
          <div
            data-testid="distances-panel"
            className="rounded-3xl border border-federasol-rose/25 bg-federasol-cream p-5 shadow-[0_14px_40px_-20px_rgba(166,93,71,0.3)] sm:p-9"
          >
            <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
              <div className="md:max-w-sm">
                <p className="eyebrow flex items-center gap-2">
                  <Footprints className="h-4 w-4" />
                  {T.location.distancesTitle}
                </p>
                <p className="mt-3 text-sm leading-relaxed text-federasol-muted">
                  {T.location.welcome}
                </p>
              </div>
              <ul className="grid flex-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 md:ml-10">
                {T.location.distances.map((d, i) => (
                  <li
                    key={d.l}
                    data-testid={`distance-item-${i}`}
                    className="flex items-center justify-between gap-3 rounded-xl bg-federasol-blush/80 px-4 py-3 text-sm"
                  >
                    <span className="leading-snug text-federasol-cocoa/80">{d.l}</span>
                    <span className="shrink-0 font-bold text-federasol-terra">{d.v}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
